/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import DataProvider, { loadDataProviderDeps } from './DataProvider';
import RelationshipGraphEventHandler from './RelationshipGraphEventHandler';
import { loadModules } from 'util/ModuleLoader';
import Config from './Config.js';
import { OVERRIDE_ACTIONS } from './RelationshipTargetModel';

let VIPR = null;
let VIPR3Provider = null;
let VIDA = null;

const NETWORK_BUNDLE_ID = 'com.ibm.vis.rave2bundlenetwork';
const VIDA_RENDER_MODE_CLIENT = 'client';
const FOCUS_FIELD_GROUP_ID_PREFIX = 'focusField×none_';
const FIELD_GROUP_ID_PREFIX = 'field×none_';
const DATA_NOES_DATASET_ID = 'data.nodes';
const HIGHLIGHTED = 'highlighted';

export default class RelationshipVisLoader {
	/**
	 * @constructor
	 */
	constructor(params) {
		this._dashboardApi = params.dashboardApi;
		this.startingPointsVM = params.startingPointsVM;
		this.viprData = null;
	}

	_loadViprModules() {
		return new Promise( (resolve, reject) => {
			loadModules(['dashboard-analytics/visualizations/vipr/VIPR', /*Though currently VIPR is prefetched by glass, as well as VIPRUtils VIDA etc.*/
				'dashboard-analytics/visualizations/definitions/providers/VIPR3Provider',
				'com/ibm/vida/controls/vida-controls'], (VIPRModule, VIPR3ProviderModule, VIDAModule) => {
				VIPR = VIPRModule;
				VIPR3Provider = VIPR3ProviderModule;
				VIDA = VIDAModule;
				resolve(true);
			}, reject);
		});
	}

	_getAjaxService(){
		return this._dashboardApi  && this._dashboardApi.getGlassCoreSvc? this._dashboardApi.getGlassCoreSvc('.Ajax') : null ;
	}

	load(el) {
		return this._loadViprModules()
			.then( loadDataProviderDeps )
			.then( () => {
				this._viprPromise = VIPR.initialize({ 'dashboardApi': this._dashboardApi });
				return this._load(el);
			});

	}

	_load(el) {
		return this._viprPromise
			.then(()=>{
				return VIPR.loadBundle(NETWORK_BUNDLE_ID);
			})
			.then((relationshipBundle)=>{
				const widget = VIPR.createWidget(el.id);
				this._widget = widget;
				this._vidaActionHandler = VIDA? new (VIDA.LegacyActionHandler.GestureToAction)(widget) : null;//VIDA Gesture Action Handler Initialization
				return widget.newViz(NETWORK_BUNDLE_ID, VIDA_RENDER_MODE_CLIENT)
					.then(() => {
						const setData = (data) => {
							return this._setWidgetData(relationshipBundle, widget, data);
						};
						const addHighlight = (columnId, targetId) => {
							return this._decorateNode({ columnId, targetId, widget, enableHighlight: true, decorationType: HIGHLIGHTED });
						};
						const removeHighlight = (columnId, targetId) => {
							return this._decorateNode({ columnId, targetId, widget, enableHighlight: false, decorationType: HIGHLIGHTED });
						};
						return {
							widget: widget,
							getEventHandler: () => this.eventHandler,
							setData: setData,
							addHighlight: addHighlight,
							removeHighlight: removeHighlight
						};
					});
			});
	}

	_setWidgetData(relationshipBundle, widget, data){
		if(data){
			//please refer explore-2602
			return this._setData(relationshipBundle, new VIPR3Provider({ dashboardApi: this._dashboardApi }), widget, data);
		}
	}


	_setData(relationshipBundle, vipr3Provider, widget, data ) {
		const { relationship } = this.startingPointsVM;
		const targetId = relationship.targetIds[0];
		if(this._isStaleRequest(data, targetId)){
			return;
		}
		const targetModel = relationship.getTargetModel();
		const lastAddedFields = targetModel && targetModel.lastAddedFields;
		const dataOptions = {
			'showConcepts': relationship.showConcepts === true,
			'targetId' : targetId,
			'addedField': lastAddedFields // seems not be used anywhere
		};
		const oDefinition = vipr3Provider.createDefinition(relationshipBundle);
		const fields = this.startingPointsVM.fields.getFields();
		const metadataInfo = fields && fields.all && fields.all.value;
		//New data provider instance
		const oDataProvider = new DataProvider(data, oDefinition, metadataInfo, dataOptions);
		const oViprData = oDataProvider.getData();
		const oSlotMapping = oDataProvider.getSlotMapping();
		if(!oViprData) {
			throw new Error('Data provider gave no data');
		}
		widget.setData( oViprData, oSlotMapping );
		const enableHighlight = true;

		//highlight field
		let fieldsToHighlight = null;
		const changedProperty = targetModel && targetModel.changedProperty;
		if(changedProperty === 'lastAddedFields') {
			fieldsToHighlight = lastAddedFields;
		} else if(changedProperty === 'lastShowRelationshipsField'){
			const { from, to } = targetModel.lastShowRelationshipsField;
			const showRelationshipsMap = targetModel.showRelationshipsMap;
			if(showRelationshipsMap[from]) {
				const found = showRelationshipsMap[from].find((entry) => entry.to === to);
				if(found && found.relationship) {
					fieldsToHighlight = [from, to];
				}
			}
		} else if(changedProperty === 'cancelAction') {
			const lastCanceledAction = targetModel.lastCanceledAction;
			if(lastCanceledAction.canceledAction === OVERRIDE_ACTIONS.DELETED) {
				fieldsToHighlight = [lastCanceledAction.fieldID];
			}
		}
		if(fieldsToHighlight && fieldsToHighlight.length) {
			fieldsToHighlight.forEach((fieldToHighlight) => {
				this._decorateNode( { columnId: fieldToHighlight, targetId, widget, enableHighlight, decorationType: HIGHLIGHTED });
			});
		}

		this._setWidgetProperties(widget);
		widget.render('client');

		const options = {
			widget: widget,
			startingPointsVM: this.startingPointsVM,
			nodeDataset: oViprData.getDataSetAt(1),
			dataProvider: oDataProvider
		};
		this.eventHandler = new RelationshipGraphEventHandler(options);
	}

	/*Highlight the node for the given column ID by decorating the corresponding datapoint*/
	_decorateNode(options){
		const { columnId, targetId, widget, enableHighlight, decorationType } = options;
		if(!columnId){
			return false;
		}
		let nodeDecorated = false;
		const lookupKey =  (targetId === columnId ? FOCUS_FIELD_GROUP_ID_PREFIX : FIELD_GROUP_ID_PREFIX ) + columnId;
		widget._data.getDataSet(DATA_NOES_DATASET_ID).getDataPoints()
			.filter((entry)=>{
				return entry.key === lookupKey;
			}).forEach((entry)=>{
				nodeDecorated = true; //TODO Optimmize this when we support multiple added fields*/
				entry.source.decorate(decorationType, enableHighlight);
			});
		return nodeDecorated;
	}

	/*
	 * Set properites such as legend, colors
	 */
	_setWidgetProperties (widget){
		widget.setProperty('widget.legend.display', Config.legend_display);
		widget.setProperty('color_node_cat' , Config.themes.default.palette.colors);
		widget.setProperty('network.links.color', Config.links_color);
		widget.setProperty('border.color',Config.border_color);
		widget.setProperty('network.nodes.size.min', Config.network_nodes_size_min);
		widget.setProperty('network.links.thickness.min', Config.network_links_thickness_min);
		widget.setProperty('network.links.thickness.max', Config.network_links_thickness_max);
	}

	unload() {
		if(this._widget) {
			this._widget.destroy();
			this._widget = null;
		}
	}

	_isStaleRequest(data , targetId){
		return (data && data.nodesDataset && data.nodesDataset.targetId !== targetId);
	}
}



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/RelationshipVisLoader.js